import {
  ButtonView,
  FileRepository,
  Plugin,
  Widget,
  toWidget,
} from "ckeditor5";
import { adminApi } from "../lib/api";

interface UploadLoaderLike {
  file: Promise<File>;
  uploadTotal?: number;
  uploaded?: number;
}

interface UploadAdapterLike {
  upload(): Promise<{ default: string }>;
  abort(): void;
}

class LiaImageUploadAdapter implements UploadAdapterLike {
  private controller = new AbortController();

  constructor(private readonly loader: UploadLoaderLike) {}

  async upload(): Promise<{ default: string }> {
    const file = await this.loader.file;
    if (!file.type.startsWith("image/")) {
      throw new Error("فایل انتخاب‌شده تصویر معتبر نیست.");
    }

    const result = await adminApi.uploads.image(file, this.controller.signal);
    return { default: result.url };
  }

  abort(): void {
    this.controller.abort();
  }
}

/**
 * Upload adapter پروژه Lia برای ImageUpload رسمی CKEditor.
 * تصاویر Drag/Drop، Paste و انتخاب از Toolbar همگی از API امن خود پروژه عبور می‌کنند.
 */
export class LiaImageUploadAdapterPlugin extends Plugin {
  static get pluginName() {
    return "LiaImageUploadAdapterPlugin" as const;
  }

  init(): void {
    const repository = this.editor.plugins.get(FileRepository);
    repository.createUploadAdapter = (loader) => new LiaImageUploadAdapter(loader as unknown as UploadLoaderLike);
  }
}

const VIDEO_ICON = `
<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
  <path d="M3.5 4.75A1.75 1.75 0 0 1 5.25 3h6.5a1.75 1.75 0 0 1 1.75 1.75v1.58l2.15-1.42A1.5 1.5 0 0 1 18 6.16v7.68a1.5 1.5 0 0 1-2.35 1.25l-2.15-1.42v1.58A1.75 1.75 0 0 1 11.75 17h-6.5a1.75 1.75 0 0 1-1.75-1.75V4.75Zm2 0v10.5h6V4.75h-6Zm8 3.98v2.54L16 12.92V7.08l-2.5 1.65Z"/>
</svg>`;

/**
 * ویدئوی آپلودی را به عنوان یک block-object واقعی در مدل CKEditor ثبت می‌کند.
 * خروجی ذخیره‌شده HTML استاندارد <video> است و URL به صورت relative باقی می‌ماند
 * تا بین محیط Development/Production قابل حمل باشد.
 */
export class LiaVideoPlugin extends Plugin {
  static get requires() {
    return [Widget] as const;
  }

  static get pluginName() {
    return "LiaVideoPlugin" as const;
  }

  init(): void {
    const editor = this.editor;
    const schema = editor.model.schema;

    schema.register("liaVideo", {
      inheritAllFrom: "$blockObject",
      allowAttributes: ["src"],
    });

    editor.conversion.for("upcast").elementToElement({
      view: {
        name: "video",
        attributes: {
          src: true,
        },
      },
      model: (viewElement, { writer }) => writer.createElement("liaVideo", {
        src: String(viewElement.getAttribute("src") ?? ""),
      }),
    });

    editor.conversion.for("dataDowncast").elementToElement({
      model: "liaVideo",
      view: (modelElement, { writer }) => writer.createContainerElement("video", {
        src: String(modelElement.getAttribute("src") ?? ""),
        controls: "controls",
        preload: "metadata",
        playsinline: "playsinline",
        class: "lia-content-video",
      }),
    });

    editor.conversion.for("editingDowncast").elementToElement({
      model: "liaVideo",
      view: (modelElement, { writer }) => {
        const video = writer.createContainerElement("video", {
          src: String(modelElement.getAttribute("src") ?? ""),
          controls: "controls",
          preload: "metadata",
          playsinline: "playsinline",
          class: "lia-content-video lia-content-video--editor",
        });

        return toWidget(video, writer, { label: "ویدئو" });
      },
    });

    editor.ui.componentFactory.add("uploadVideo", (locale): any => {
      const button = new ButtonView(locale);
      button.set({
        label: "آپلود ویدئو",
        tooltip: true,
        icon: VIDEO_ICON,
      });

      this.listenTo(button, "execute", async () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "video/mp4,video/webm,video/ogg";

        input.addEventListener("change", async () => {
          const file = input.files?.[0];
          if (!file) return;

          button.isEnabled = false;
          button.label = "در حال آپلود…";

          try {
            const result = await adminApi.uploads.video(file);
            editor.model.change((writer) => {
              const video = writer.createElement("liaVideo", { src: result.url });
              editor.model.insertObject(video, null, null, { setSelection: "on" });
            });
          } catch (error) {
            const message = error instanceof Error ? error.message : "آپلود ویدئو انجام نشد.";
            window.dispatchEvent(new CustomEvent("lia:ckeditor-video-error", { detail: message }));
          } finally {
            button.isEnabled = true;
            button.label = "آپلود ویدئو";
          }
        }, { once: true });

        input.click();
      });
    });
  }
}
